import React from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Sun, BatteryCharging, Truck, Building2, Zap } from "lucide-react";
import Layout from "@/components/layout/Layout";
import { EaasFlow } from "@/components/sections/Sections";

const TIERS = [
  {
    name: "Pay-per-kWh",
    price: "$0.16 – $0.22",
    sub: "per delivered kWh",
    badge: "MOST FLEXIBLE",
    features: [
      "Pay only what you consume",
      "Zero upfront capital",
      "Tratou owns & maintains the asset",
      "24/7 remote monitoring",
      "30 day flexible contract",
    ],
  },
  {
    name: "Monthly Subscription",
    price: "From $4,800",
    sub: "per MBESS / month",
    badge: "BEST VALUE",
    highlight: true,
    features: [
      "Dedicated MBESS unit on-site",
      "Unlimited cycling included",
      "Solar hybrid option",
      "Priority SLA — 4hr response",
      "Quarterly business review",
    ],
  },
  {
    name: "Project-Based",
    price: "Custom",
    sub: "per scope of work",
    badge: "TURN-KEY",
    features: [
      "End-to-end project delivery",
      "Site engineering & permits",
      "Multiple MBESS units",
      "Solar integration option",
      "Build-Own-Operate-Transfer",
    ],
  },
];

export default function EaaS() {
  return (
    <Layout>
      <section className="py-28 border-b border-white/10" data-testid="eaas-hero">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <span className="eyebrow">Energy-as-a-Service</span>
          <h1 className="heading-massive mt-4">
            Don't buy power. <span className="text-tratou-green">Subscribe to it.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-tratou-dim text-lg">
            Tratou Energy operates the largest fleet of Mobile Battery Storage Systems in the region.
            You get delivered kilowatt-hours on-site, billed like a utility — no CapEx, no maintenance,
            no diesel logistics.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/contact?intent=quote" className="btn-primary" data-testid="eaas-hero-quote">Request EaaS Proposal <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/roi" className="btn-secondary" data-testid="eaas-hero-roi">Calculate ROI</Link>
          </div>
        </div>
      </section>

      <EaasFlow />

      <section className="py-24 border-t border-white/10" data-testid="eaas-tiers">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <span className="eyebrow">Commercial Models</span>
          <h2 className="heading-section mt-4">Three ways to plug in.</h2>

          <div className="mt-12 grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {TIERS.map((t) => (
              <div
                key={t.name}
                className={`bg-tratou-deep p-8 panel-hover relative ${t.highlight ? "lg:scale-[1.02] border border-tratou-green/40" : ""}`}
                data-testid={`tier-${t.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <p className="eyebrow">{t.badge}</p>
                <h3 className="mt-4 font-display text-2xl font-bold">{t.name}</h3>
                <p className="mt-6 font-display text-4xl font-bold text-tratou-green">{t.price}</p>
                <p className="font-mono text-xs uppercase tracking-[0.18em] text-tratou-dim mt-1">{t.sub}</p>

                <ul className="mt-7 space-y-3">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-white">
                      <Check className="w-4 h-4 mt-0.5 text-tratou-green flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/contact?intent=quote"
                  className={`mt-8 inline-flex w-full justify-center ${t.highlight ? "btn-primary" : "btn-secondary"}`}
                  data-testid={`tier-cta-${t.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  Get Started
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" data-testid="eaas-why">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="eyebrow">Why EaaS</span>
            <h2 className="heading-section mt-4">Convert <span className="text-tratou-green">CapEx</span> into a predictable OpEx line.</h2>
            <p className="mt-6 text-tratou-dim text-lg">
              Traditional power procurement locks capital, ties up maintenance teams, and exposes you to
              fuel volatility. EaaS gives you grid-quality electricity on-demand, billed per kilowatt-hour.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                "Zero upfront capital expenditure",
                "Predictable monthly billing",
                "Carbon and ESG reporting built-in",
                "Tratou handles charging, swapping, maintenance",
                "Scale up or down as your project evolves",
              ].map((f) => (
                <li key={f} className="flex items-start gap-3 text-white">
                  <Check className="w-5 h-5 mt-0.5 text-tratou-green flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="panel aspect-square overflow-hidden">
            <img src="https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1400&q=80" alt="Solar farm" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>
    </Layout>
  );
}
